import { C } from '../theme'
import { grp, signedUsd, usd } from '../lib/format'
import { Action, BlockHead, Note, Strong } from './ui/primitives'
import type { PnlLine, Snapshot } from '../types'

function lineStyle(kind: PnlLine['kind']) {
  switch (kind) {
    case 'total':
      return { fontSize: 26, fontWeight: 700, border: C.text, labelColor: C.text }
    case 'subtotal':
      return { fontSize: 19, fontWeight: 600, border: C.ruleStrong, labelColor: C.text }
    default:
      return { fontSize: 17, fontWeight: 400, border: C.rule, labelColor: C.muted2 }
  }
}

export function Today({
  snap,
  open,
  onToggle,
  showNotes,
}: {
  snap: Snapshot
  open: boolean
  onToggle: () => void
  showNotes: boolean
}) {
  const t = snap.today
  const summary = [
    { l: 'fills', v: grp(t.fills), c: C.text },
    { l: 'turnover', v: usd(t.turnoverUsd, 0), c: C.text },
    { l: 'gross edge', v: signedUsd(t.grossUsd, 2), c: t.grossUsd >= 0 ? C.pos : C.neg },
    { l: 'net after gas', v: signedUsd(t.netUsd, 2), c: t.netUsd >= 0 ? C.pos : C.neg },
  ]

  return (
    <div style={{ minWidth: 0 }}>
      <BlockHead
        title="Today"
        right={<Action onClick={onToggle}>{open ? 'summary' : 'breakdown'}</Action>}
      />

      {!open &&
        summary.map((r) => (
          <div
            key={r.l}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 14,
              padding: '15px 0',
              borderBottom: `1px solid ${C.rule}`,
            }}
          >
            <span style={{ fontSize: 15, color: C.muted2 }}>{r.l}</span>
            <span
              style={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: r.c,
                whiteSpace: 'nowrap',
              }}
            >
              {r.v}
            </span>
          </div>
        ))}

      {!open && showNotes && (
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginTop: 12 }}>
          Net is gross edge minus fees, interest and our gas. Solver gas is paid by the solver.
        </div>
      )}

      {open &&
        snap.pnl.map((p) => {
          const st = lineStyle(p.kind)
          return (
            <div
              key={p.label}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) auto',
                gap: 12,
                alignItems: 'baseline',
                padding: '11px 0',
                borderBottom: `1px solid ${st.border}`,
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  color: st.labelColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.label}
                <span style={{ fontSize: 13, color: C.muted2, paddingLeft: 8 }}>{p.note}</span>
              </span>
              <span
                style={{
                  fontSize: st.fontSize,
                  fontWeight: st.fontWeight,
                  whiteSpace: 'nowrap',
                  color:
                    p.valueUsd == null
                      ? C.muted2
                      : p.valueUsd >= 0
                        ? C.pos
                        : C.neg,
                }}
              >
                {p.valueUsd == null ? 'n/a' : signedUsd(p.valueUsd, 2)}
              </span>
            </div>
          )
        })}

      {open && showNotes && (
        <Note maxWidth={640}>
          Route competitiveness is a separate model — it describes how the maker's gross becomes the
          user's net — and is <Strong>never subtracted from this cash flow</Strong>. Realized edge
          by ticket bucket — −4.59 bps under $2k, −2.96 bps to $10k, −1.52 bps above — is a 30-day
          measurement of the legacy bot on the old contracts, not of mm-v2.
        </Note>
      )}
    </div>
  )
}
